import { useCallback, useRef } from 'react'
import { toast } from 'sonner'
import { confirmBooking, fetchInbox, setOrderStatus } from '../adminApi'
import { useLatestLoad } from '../../hooks/useLatestLoad'
import { errorMessage } from '../../core/errors'
import { ApiError } from '../../core/api/http'
import type { FulfilmentStatus, InboxBooking, InboxOrder } from '../../core/types/adminRequests'
import type { Tracking } from '../../core/types/account'

/** De inbox van het beheer: laden, en per actie direct bewaren (met herstel bij een fout). */
export function useInbox() {
  const { data: inbox, setData: setInbox, status, reload } = useLatestLoad(fetchInbox)

  const putOrder = useCallback(
    (o: InboxOrder) => setInbox((i) => (i ? { ...i, orders: i.orders.map((x) => (x.id === o.id ? o : x)) } : i)),
    [setInbox],
  )
  const putBooking = useCallback(
    (b: InboxBooking) => setInbox((i) => (i ? { ...i, bookings: i.bookings.map((x) => (x.id === b.id ? b : x)) } : i)),
    [setInbox],
  )

  // Eén actie per bestelling of afspraak tegelijk: een dubbele klik op "Ingepakt" of "Bevestigen"
  // vertrekt anders vanaf dezelfde oude stand, en de fout van de eerste draait de tweede terug.
  const inFlight = useRef(new Set<string>())

  const advanceOrder = useCallback(
    async (order: InboxOrder, next: FulfilmentStatus, tracking?: Tracking) => {
      const key = `order:${order.id}`
      if (inFlight.current.has(key)) return false
      inFlight.current.add(key)
      putOrder({ ...order, status: next, ...(tracking ? { tracking } : {}) })
      try {
        putOrder(await setOrderStatus(order.id, next, tracking))
        return true
      } catch (err) {
        putOrder(order)
        toast.error('Dat lukte niet', { description: errorMessage(err) })
        // Verouderd of verdwenen (ander tabblad, tweede apparaat): de echte stand ophalen,
        // anders blijft de oude knop staan en loopt elke volgende klik weer op dezelfde fout.
        if (err instanceof ApiError && (err.status === 404 || err.status === 409)) reload()
        return false
      } finally {
        inFlight.current.delete(key)
      }
    },
    [putOrder, reload],
  )

  const confirm = useCallback(
    async (booking: InboxBooking) => {
      const key = `booking:${booking.id}`
      if (inFlight.current.has(key)) return false
      inFlight.current.add(key)
      putBooking({ ...booking, status: 'bevestigd' })
      try {
        putBooking(await confirmBooking(booking))
        return true
      } catch (err) {
        putBooking(booking)
        toast.error('Bevestigen lukte niet', { description: errorMessage(err) })
        return false
      } finally {
        inFlight.current.delete(key)
      }
    },
    [putBooking],
  )

  return { inbox, status, reload, advanceOrder, confirm }
}
